import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/Layout';
import { useSettings } from '../hooks/queries';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, X, GripVertical, Upload, Image, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SETTINGS = {
  app_name: process.env.REACT_APP_APP_NAME || 'RajjePulse',
  login_title: 'Welcome Back',
  login_subtitle: 'Sign in with Google to access your campaign dashboard',
  login_tagline: 'Campaign Management System',
  login_image_url: '',
  login_logo_url: '',
  slider_enabled: false,
  slider_cards: [],
  pledge_status_options: ['Pledge', 'Against', 'Undecided', 'NA', 'WN', 'SO', 'OC']
};

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const { data: serverSettings, isLoading: loading } = useSettings();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [newStatusOption, setNewStatusOption] = useState('');
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (serverSettings) {
      setSettings({
        ...DEFAULT_SETTINGS,
        ...serverSettings,
        login_logo_url: serverSettings.login_logo_url || '',
        slider_enabled: serverSettings.slider_enabled || false,
        slider_cards: serverSettings.slider_cards || [],
        pledge_status_options: serverSettings.pledge_status_options || DEFAULT_SETTINGS.pledge_status_options
      });
    }
  }, [serverSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`${API}/settings`, settings);
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Use full URL for the uploaded image
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      setSettings({ ...settings, login_image_url: imageUrl });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const logoUrl = `${BACKEND_URL}${response.data.url}`;
      setSettings({ ...settings, login_logo_url: logoUrl });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const addSliderCard = () => {
    if (settings.slider_cards.length >= 4) {
      toast.error('Maximum 4 slides allowed');
      return;
    }
    setSettings({
      ...settings,
      slider_cards: [...settings.slider_cards, { title: '', description: '', image_url: '' }]
    });
  };

  const updateSliderCard = (index, field, value) => {
    const updatedCards = [...settings.slider_cards];
    updatedCards[index] = { ...updatedCards[index], [field]: value };
    setSettings({ ...settings, slider_cards: updatedCards });
  };

  const removeSliderCard = (index) => {
    const updatedCards = settings.slider_cards.filter((_, i) => i !== index);
    setSettings({ ...settings, slider_cards: updatedCards });
  };

  const addStatusOption = () => {
    if (!newStatusOption.trim()) {
      toast.error('Please enter a status name');
      return;
    }
    if (settings.pledge_status_options.includes(newStatusOption.trim())) {
      toast.error('This status already exists');
      return;
    }
    setSettings({
      ...settings,
      pledge_status_options: [...settings.pledge_status_options, newStatusOption.trim()]
    });
    setNewStatusOption('');
  };

  const removeStatusOption = (index) => {
    const newOptions = settings.pledge_status_options.filter((_, i) => i !== index);
    setSettings({ ...settings, pledge_status_options: newOptions });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8">
        <div className="mb-6">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            App Settings
          </h1>
          <p className="text-muted-foreground">Customize your application</p>
        </div>

        <div className="max-w-2xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Application Name</Label>
                <Input
                  value={settings.app_name}
                  onChange={(e) => setSettings({ ...settings, app_name: e.target.value })}
                  placeholder="e.g., RajjePulse"
                />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input
                  value={settings.login_tagline}
                  onChange={(e) => setSettings({ ...settings, login_tagline: e.target.value })}
                  placeholder="e.g., Campaign Management System"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Displayed below the app name on the login page
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Logo Upload Section */}
          <Card>
            <CardHeader>
              <CardTitle>Login Page Logo</CardTitle>
              <CardDescription>
                Upload a logo to display on the login page (recommended: square PNG with transparent background)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  ref={logoInputRef}
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </Button>
                {settings.login_logo_url && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setSettings({ ...settings, login_logo_url: '' })}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                )}
              </div>
              
              {settings.login_logo_url && (
                <div className="p-4 bg-[#0a1628] rounded-lg inline-block">
                  <img
                    src={settings.login_logo_url}
                    alt="Logo preview"
                    className="h-24 w-24 object-contain"
                    onError={(e) => {
                      e.target.src = '';
                      toast.error('Failed to load logo');
                    }}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Slider Cards Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Login Page Announcements</span>
                <div className="flex items-center gap-2">
                  <Label htmlFor="slider-toggle" className="text-sm font-normal">Enable Slider</Label>
                  <Switch
                    id="slider-toggle"
                    checked={settings.slider_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, slider_enabled: checked })}
                  />
                </div>
              </CardTitle>
              <CardDescription>
                Add up to 4 announcement slides on the login page (optional)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings.slider_enabled && (
                <>
                  {settings.slider_cards.map((card, index) => (
                    <div key={index} className="p-4 border rounded-lg space-y-3 relative">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                        onClick={() => removeSliderCard(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <div className="pr-8">
                        <Label>Slide {index + 1} Title</Label>
                        <Input
                          value={card.title}
                          onChange={(e) => updateSliderCard(index, 'title', e.target.value)}
                          placeholder="e.g., Important Announcement"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={card.description}
                          onChange={(e) => updateSliderCard(index, 'description', e.target.value)}
                          placeholder="e.g., Voting day is approaching..."
                        />
                      </div>
                    </div>
                  ))}
                  
                  {settings.slider_cards.length < 4 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addSliderCard}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Slide ({settings.slider_cards.length}/4)
                    </Button>
                  )}
                </>
              )}
              
              {!settings.slider_enabled && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Enable the slider toggle above to add announcement slides
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Login Page Customization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Login Page Title</Label>
                <Input
                  value={settings.login_title}
                  onChange={(e) => setSettings({ ...settings, login_title: e.target.value })}
                  placeholder="e.g., Welcome Back"
                />
              </div>
              <div>
                <Label>Login Page Subtitle</Label>
                <Input
                  value={settings.login_subtitle}
                  onChange={(e) => setSettings({ ...settings, login_subtitle: e.target.value })}
                  placeholder="e.g., Sign in to access your dashboard"
                />
              </div>
              
              <div>
                <Label>Login Page Background Image</Label>
                <div className="mt-2 space-y-3">
                  {/* File Upload */}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Image from Device'}
                    </Button>
                  </div>
                  
                  {/* Or use URL */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 border-t border-gray-200"></div>
                    <span className="text-xs text-muted-foreground">OR</span>
                    <div className="flex-1 border-t border-gray-200"></div>
                  </div>
                  
                  <div>
                    <Input
                      value={settings.login_image_url}
                      onChange={(e) => setSettings({ ...settings, login_image_url: e.target.value })}
                      placeholder="Paste image URL (e.g., https://images.unsplash.com/...)"
                    />
                  </div>
                </div>
              </div>
              
              {/* Image Preview */}
              {settings.login_image_url && (
                <div>
                  <Label>Preview</Label>
                  <div className="relative mt-2">
                    <img
                      src={settings.login_image_url}
                      alt="Login background preview"
                      className="w-full h-48 object-cover rounded-lg border"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1581592717535-7f3e001bfa7f?w=800';
                        toast.error('Failed to load image');
                      }}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setSettings({ ...settings, login_image_url: '' })}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pledge Status Options</CardTitle>
              <CardDescription>
                Manage the dropdown options for constituent pledge status
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                {settings.pledge_status_options.map((option, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-2 p-2 bg-secondary rounded-lg group"
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1">{option}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
                      onClick={() => removeStatusOption(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  value={newStatusOption}
                  onChange={(e) => setNewStatusOption(e.target.value)}
                  placeholder="New status option..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addStatusOption();
                    }
                  }}
                />
                <Button onClick={addStatusOption} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Note: Pledge, Against, and Undecided are typically considered "Reached" status. 
                Other statuses like NA, WN, SO, OC are "Unreached".
              </p>
            </CardContent>
          </Card>

          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
